import React, { useState, useEffect, useMemo } from 'react';
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FaArrowUp } from 'react-icons/fa';

// DebouncedInput component for filtering
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
  );
};

// Filter component for column filtering
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant } = column.columnDef.meta ?? {};

  if (filterVariant === 'range') {
    return (
      <div>
        <div className="flex space-x-2">
          <DebouncedInput
            type="number"
            value={columnFilterValue?.[0] ?? ''}
            onChange={(value) =>
              column.setFilterValue((old) => [value, old?.[1]])
            }
            placeholder="Min"
            className="w-24 border border-border px-2 rounded"
          />
          <DebouncedInput
            type="number"
            value={columnFilterValue?.[1] ?? ''}
            onChange={(value) =>
              column.setFilterValue((old) => [old?.[0], value])
            }
            placeholder="Max"
            className="w-24 border border-border px-2 rounded"
          />
        </div>
        <div className="h-1" />
      </div>
    );
  }

  if (filterVariant === 'date') {
    return (
      <div>
        <div className="flex flex-col">
          <input
            type="date"
            value={columnFilterValue?.[0] ?? ''}
            onChange={(e) =>
              column.setFilterValue((old) => [e.target.value, old?.[1]])
            }
            placeholder="From"
            className="w-36 border border-border px-2 rounded"
          />
          to
          <input
            type="date"
            value={columnFilterValue?.[1] ?? ''}
            onChange={(e) =>
              column.setFilterValue((old) => [old?.[0], e.target.value])
            }
            placeholder="To"
            className="w-36 border border-border px-2 rounded"
          />
        </div>
        <div className="h-1" />
      </div>
    );
  }

  if (filterVariant === 'select') {
    return (
      <select
        // onChange={(e) => column.setFilterValue(e.target.value)}
        value={columnFilterValue?.toString()}
        className="border border-secondary px-2 rounded p-1 text-base font-medium"
        onChange={(e) => {
          const value = e.target.value === '' ? undefined :
            e.target.value === 'true' ? true :
              e.target.value === 'false' ? false :
                e.target.value;
          column.setFilterValue(value);
        }}
      >
        <option value="">All</option>
        {column.columnDef.meta?.filterOptions?.map((option) => (
          <option key={option.value} value={option.value} className='text-base font-medium'>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <DebouncedInput
      className="w-36 border border-border px-2 rounded"
      onChange={(value) => column.setFilterValue(value)}
      placeholder="Search..."
      type="text"
      value={(columnFilterValue ?? '')}
    />
  );
};

/**
 * GenericTable - A reusable table component using Tanstack Table
 * 
 * @param {Object} props
 * @param {Array} props.data - The data to display in the table
 * @param {Array} props.columns - The column definitions
 * @param {boolean} props.enableFiltering - Enable column filtering
 * @param {boolean} props.enableSorting - Enable column sorting
 * @param {boolean} props.enablePagination - Enable pagination
 * @param {Array} props.pageSizeOptions - Available page size options
 * @param {number} props.defaultPageSize - Default page size
 */
const TanStackTable = ({
  data = [],
  columns = [],
  enableFiltering = true,
  enableSorting = true,
  enablePagination = true,
  pageSizeOptions = [10, 20, 30, 50, 100],
  defaultPageSize = 10,
}) => {
  const [columnFilters, setColumnFilters] = useState([]);

  const tableColumns = useMemo(() => columns, [columns]);
  const tableData = useMemo(() => data, [data]);

  const [showFilters, setShowFilters] = useState(false);

  // Add date filtering handlers to columns that need it
  const columnsWithDateFiltering = useMemo(() => {
    return tableColumns.map(column => {
      if (column.meta?.filterVariant === 'date') {
        return {
          ...column,
          filterFn: (row, columnId, filterValue) => {
            // Skip filtering if no date filter values are set
            if (!filterValue || !filterValue[0] && !filterValue[1]) {
              return true;
            }

            const cellValue = row.getValue(columnId);
            if (!cellValue) return false;

            const cellDate = new Date(cellValue);

            // Check if date is in range
            if (filterValue[0] && filterValue[1]) {
              const startDate = new Date(filterValue[0]);
              const endDate = new Date(filterValue[1]);
              endDate.setHours(23, 59, 59, 999); // Include the whole end day
              return cellDate >= startDate && cellDate <= endDate;
            }
            // Check if date is after start date
            else if (filterValue[0]) {
              const startDate = new Date(filterValue[0]);
              return cellDate >= startDate;
            }
            // Check if date is before end date
            else if (filterValue[1]) {
              const endDate = new Date(filterValue[1]);
              endDate.setHours(23, 59, 59, 999); // Include the whole end day
              return cellDate <= endDate;
            }

            return true;
          }
        };
      }
      return column;
    });
  }, [tableColumns]);

  const table = useReactTable({
    data: tableData,
    columns: columnsWithDateFiltering,
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    initialState: {
      pagination: {
        pageSize: defaultPageSize,
      },
    },
  });

  return (
    <div className="generic-table-container">
      <table className="w-full border-collapse overflow-visible text-base" role="table">
        {/* Filter row */}
        {/* {enableFiltering && (
          <thead>
            <p className='text-primary text-xl font-bold mb-2 ml-6 cursor-pointer' onClick={() => setShowFilters(!showFilters)}>Filters:</p>
            {
              showFilters && (
                table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id + '-filters'}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id + '-filter'} className='p-3 text-left'>
                        <div className='flex flex-col items-start '>
                          {header.isPlaceholder ? null : (
                            <div className="">
                              <div className="font-semibold text-left">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </div>
                            </div>
                          )}
                          {header.column.getCanFilter() ? (
                            <div className="">
                              <Filter column={header.column} />
                            </div>
                          ) : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))
              )
            }
          </thead>
        )} */}
        <thead className="bg-navBtn">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  className={`p-3 ${header.column.columnDef.align === 'center'
                    ? 'text-center'
                    : header.column.columnDef.align === 'right'
                      ? 'text-right'
                      : 'text-left'
                    }`}
                >
                  {header.isPlaceholder ? null : (
                    <>
                      <div
                        className={
                          enableSorting && header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                        }
                        onClick={
                          enableSorting
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: <FaArrowUp className='inline ml-1 size-3' />,
                          desc: <FaArrowUp className='rotate-180 inline ml-1 size-3' />,
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    </>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b border-navBtn hover:bg-gray-50 relative">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`p-3 ${cell.column.columnDef.align === 'center'
                      ? 'text-center'
                      : cell.column.columnDef.align === 'right'
                        ? 'text-right'
                        : 'text-left'
                    }`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="text-center p-4 border-b border-border"
              >
                No results found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {enablePagination && (
        <div className="m-5 flex flex-wrap flex-row-reverse items-center justify-between gap-2 text-base">
          <div className='flex items-center gap-2'>
            <button
              className="border border-secondary rounded p-1 px-2 disabled:opacity-50"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              {"<<"}
            </button>
            <button
              className="border border-secondary rounded p-1 px-2 disabled:opacity-50"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {"<"}
            </button>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount() || 1}
              </strong>
            </span>
            <button
              className="border border-secondary rounded p-1 px-2 disabled:opacity-50"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {">"}
            </button>
            <button
              className="border border-secondary rounded p-1 px-2 disabled:opacity-50"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              {">>"}
            </button>
            <span className="flex items-center gap-1">
              | Go to page:
              <input
                type="number"
                min="1"
                max={table.getPageCount()}
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                className="border border-secondary p-1 rounded w-16"
              />
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              className="border border-secondary p-1 rounded bg-navBtn"
            >
              {pageSizeOptions.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div className="">
            {table.getPrePaginationRowModel().rows.length} Total Rows
          </div>
        </div>
      )}
    </div>
  );
};

export default TanStackTable;